import { supabase } from '@/integrations/supabase/client';
import { audioCacheService } from './audioCache';
import { formatTextForTTS } from '@/utils/textToSpeechFormatter';
import { debugLog } from '@/utils/debugLogger';
import { SUPABASE_CONFIG } from '@/config/supabaseConfig';

export interface TTSOptions {
  text: string;
  contentType: 'blog' | 'quotes' | 'predictions';
  contentId: string;
  language?: string;
  voice?: string;
}

export interface TTSResult {
  audioUrl: string;
  cached: boolean;
}

class TTSService {
  private readonly edgeFunctionUrl: string;

  constructor() {
    // Use environment variable for Supabase URL
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || SUPABASE_CONFIG.URL;
    this.edgeFunctionUrl = `${supabaseUrl}/functions/v1/text-to-speech-cached`;
  }

  async generateSpeech(options: TTSOptions): Promise<TTSResult> {
    const { text, contentType, contentId, language = 'it-IT', voice = 'it-IT-Standard-A' } = options;
    
    // Format text for optimal TTS pronunciation
    const formattedText = formatTextForTTS(text);

    // Check cache first using formatted text
    const cachedEntry = audioCacheService.get(contentType, contentId, formattedText);
    if (cachedEntry) {
      debugLog('audio', `Cache HIT for ${contentType}:${contentId}`, undefined, 'TTS Service');
      
      // Create fresh blob URL from cached base64 data
      const audioBytes = Uint8Array.from(atob(cachedEntry.audioData), c => c.charCodeAt(0));
      const audioBlob = new Blob([audioBytes], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      return {
        audioUrl,
        cached: true,
      };
    }

    debugLog('audio', `Cache MISS for ${contentType}:${contentId}, calling Edge Function`, undefined, 'TTS Service');

    try {
      // TTS is now public - no authentication required
      debugLog('audio', `Calling public TTS endpoint for ${contentType}:${contentId}`, undefined, 'TTS Service');
      
      const response = await fetch(this.edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: formattedText,
          contentType,
          contentId,
          language,
          voice,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`TTS API error: ${response.status} - ${errorData.error || 'Unknown error'}`);
      }

      // Parse JSON response with base64 audio content
      const responseData = await response.json();
      
      if (!responseData.audioContent) {
        throw new Error('No audio content received from TTS service');
      }

      // Convert base64 to blob and create object URL
      const audioBytes = Uint8Array.from(atob(responseData.audioContent), c => c.charCodeAt(0));
      const audioBlob = new Blob([audioBytes], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);

      // Cache the base64 data instead of the blob URL
      audioCacheService.set(contentType, contentId, formattedText, responseData.audioContent);

      debugLog('audio', `Generated and cached audio for ${contentType}:${contentId}`, undefined, 'TTS Service');

      return {
        audioUrl,
        cached: false,
      };

    } catch (error) {
      console.error('[TTS Service] Error generating speech:', error);
      throw error;
    }
  }

  async generateBlogSpeech(slug: string, text: string): Promise<TTSResult> {
    return this.generateSpeech({
      text,
      contentType: 'blog',
      contentId: slug,
    });
  }

  async generateQuotesSpeech(metal: string, price: number, timestamp: string): Promise<TTSResult> {
    const text = `Il prezzo attuale dell'${metal.toLowerCase()} è ${price.toFixed(2)} euro al grammo.`;
    const contentId = `${metal}_${timestamp}`;
    
    return this.generateSpeech({
      text,
      contentType: 'quotes',
      contentId,
    });
  }

  async generatePredictionSpeech(predictionId: string, predictionText: string): Promise<TTSResult> {
    return this.generateSpeech({
      text: predictionText,
      contentType: 'predictions',
      contentId: predictionId,
    });
  }

  // Invalidation methods that should be called when content changes
  invalidateQuotesCache(): void {
    audioCacheService.invalidateQuotes();
  }

  invalidatePredictionsCache(predictionId?: string): void {
    audioCacheService.invalidatePredictions(predictionId);
  }

  invalidateBlogCache(slug: string): void {
    audioCacheService.invalidateBlogPost(slug);
  }

  // Cleanup method to revoke object URLs
  cleanupAudioUrl(audioUrl: string): void {
    try {
      URL.revokeObjectURL(audioUrl);
    } catch (error) {
      console.warn('[TTS Service] Failed to revoke object URL:', error);
    }
  }
}

export const ttsService = new TTSService();